export type AvailabilityScope = 'global' | 'china' | 'europe' | 'north-america' | 'asia' | 'australia' | 'middle-east' | 'western_europe' | 'northern_europe' | 'france' | 'germany' | 'spain' | 'italy' | 'uk' | 'anglophone';

export interface PlatformLink {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: string;
  availability: AvailabilityScope[];
  tags?: string[];
  isPremium?: boolean;
}

export interface PlatformCategory {
  id: string;
  title: string;
  subtitle: string;
  highlight?: string;
  colorFrom: string;
  colorTo: string;
  platforms: PlatformLink[];
}

export const videoCategories: PlatformCategory[] = [
  {
    id: 'streaming-vod',
    title: 'Streaming & VOD',
    subtitle: 'Films, séries, TV en direct et contenus à la demande',
    highlight: 'Qualité 4K & Dolby Atmos',
    colorFrom: 'from-slate-900',
    colorTo: 'to-cyan-500',
    platforms: [
      {
        id: 'apple-tv',
        name: 'Apple TV+',
        description: 'Productions originales Apple de qualité cinéma.',
        url: 'https://tv.apple.com',
        icon: 'https://cdn.simpleicons.org/appletv/000000?v=1763029785513',
        availability: ['global'],
        tags: ['Originals', '4K'],
      },
      {
        id: 'disney-plus',
        name: 'Disney+',
        description: 'Marvel, Star Wars, Disney et contenus familiaux.',
        url: 'https://www.disneyplus.com',
        icon: '/icons/disney-plus.svg',
        availability: ['global'],
        tags: ['Famille', '4K'],
      },
      {
        id: 'hbo-max',
        name: 'Max (HBO)',
        description: 'HBO, Warner Bros et DC Comics.',
        url: 'https://www.max.com',
        icon: 'https://cdn.simpleicons.org/max/002BE7?v=1763029785513',
        availability: ['global'],
        tags: ['Premium', 'Originals'],
      },
      {
        id: 'netflix',
        name: 'Netflix',
        description: 'Films, séries et documentaires exclusifs.',
        url: 'https://www.netflix.com',
        icon: '/icons/netflix.svg',
        availability: ['global'],
        tags: ['4K', 'Dolby Vision'],
      },
      {
        id: 'canal-plus',
        name: 'Canal+',
        description: 'Cinéma, séries, sport et documentaires premium français.',
        url: 'https://www.canalplus.com',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/1/1a/Canal%2B.svg?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Premium', 'Sport', 'Cinéma'],
      },
      {
        id: 'oqee-tv',
        name: 'OQEE by Free',
        description: 'TV Freebox avec live, replay et VOD pour abonnés Free.',
        url: 'https://www.oqee.tv',
        icon: '/icons/oqee.svg',
        availability: ['france', 'europe'],
        tags: ['TV', 'Free', 'Replay'],
      },
      {
        id: 'xpeng-iptv-player',
        name: 'XPENG IPTV Player',
        description: '🎯 Player IPTV intégré - M3U & Xtream avec sauvegarde automatique de vos identifiants.',
        url: 'https://dlnraja.github.io/xpengmedia/iptv.html',
        icon: 'https://cdn.simpleicons.org/livestream/CF3A2C?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'M3U', 'Xtream', 'XPENG Intégré'],
      },
      {
        id: 'ottplayer',
        name: 'OTTPlayer.tv',
        description: 'Lecteur IPTV web - Ajoutez vos playlists M3U directement dans le navigateur.',
        url: 'https://ottplayer.tv',
        icon: 'https://cdn.simpleicons.org/livewire/FF00A0?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'M3U', 'Web Player'],
      },
      {
        id: 'iptv-org-player',
        name: 'IPTV.org Player',
        description: 'Player IPTV web open-source avec support M3U/M3U8.',
        url: 'https://iptv-org.github.io/iptv-org.github.io/',
        icon: 'https://cdn.simpleicons.org/libreoffice/18A303?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'Open Source', 'M3U8'],
      },
      {
        id: 'freestreams-live',
        name: 'FreeStreams Live',
        description: 'Lecteur IPTV web gratuit pour chaînes live du monde entier.',
        url: 'https://freestreams-live1.tv',
        icon: 'https://cdn.simpleicons.org/livetv/FF0000?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'Gratuit', 'Live'],
      },
      {
        id: 'eja-tv',
        name: 'Eja.TV',
        description: 'Player IPTV web avec EPG et support multi-formats.',
        url: 'https://eja.tv',
        icon: 'https://cdn.simpleicons.org/tidal/000000?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'EPG', 'Web'],
      },
      {
        id: 'whostreams',
        name: 'WhoStreams.Live',
        description: 'Agrégateur IPTV web - Trouvez et regardez des chaînes live.',
        url: 'https://whostreams.live',
        icon: 'https://cdn.simpleicons.org/webrtc/333333?v=1763029785513',
        availability: ['global'],
        tags: ['IPTV', 'Agrégateur', 'Live TV'],
      },
      {
        id: 'paramount-plus',
        name: 'Paramount+',
        description: 'Films Paramount, CBS et contenus exclusifs.',
        url: 'https://www.paramountplus.com',
        icon: 'https://cdn.simpleicons.org/paramountplus/0064FF?v=1763029785513',
        availability: ['global'],
        tags: ['Films', 'Séries'],
      },
      {
        id: 'prime-video',
        name: 'Prime Video',
        description: 'Blockbusters et séries Amazon Originals.',
        url: 'https://www.primevideo.com',
        icon: '/icons/prime-video.svg',
        availability: ['global'],
        tags: ['Prime', 'Originals'],
      },
      {
        id: 'youtube',
        name: 'YouTube',
        description: 'Vidéos, live et chaînes du monde entier.',
        url: 'https://www.youtube.com',
        icon: 'https://cdn.simpleicons.org/youtube/FF0000?v=1763029785513',
        availability: ['global'],
        tags: ['Live', 'Shorts'],
      },
      {
        id: 'peacock',
        name: 'Peacock',
        description: 'NBC Universal avec sports et originals.',
        url: 'https://www.peacocktv.com',
        icon: 'https://cdn.simpleicons.org/peacock/000000?v=1763029785513',
        availability: ['north-america'],
        tags: ['Sports', 'Gratuit'],
      },
      {
        id: 'twitch',
        name: 'Twitch',
        description: 'Streaming live gaming, musique et IRL.',
        url: 'https://www.twitch.tv',
        icon: 'https://cdn.simpleicons.org/twitch/9146FF?v=1763029785513',
        availability: ['global'],
        tags: ['Live', 'Gaming'],
      },
      {
        id: 'vimeo',
        name: 'Vimeo',
        description: 'Plateforme vidéo pour créateurs et artistes.',
        url: 'https://vimeo.com',
        icon: 'https://cdn.simpleicons.org/vimeo/1AB7EA?v=1763029785513',
        availability: ['global'],
        tags: ['Créateurs', 'HD'],
      },
      {
        id: 'crunchyroll',
        name: 'Crunchyroll',
        description: 'Leader mondial du streaming d\'anime légal avec catalogue énorme.',
        url: 'https://www.crunchyroll.com',
        icon: 'https://cdn.simpleicons.org/crunchyroll/F47521?v=1763029785513',
        availability: ['global'],
        tags: ['Anime', 'Manga', 'Premium'],
      },
      {
        id: 'hidive',
        name: 'HIDIVE',
        description: 'Anime de niche et titres exclusifs en streaming.',
        url: 'https://www.hidive.com',
        icon: 'https://cdn.simpleicons.org/anime/FF0000?v=1763029785513',
        availability: ['global'],
        tags: ['Anime', 'Exclusif'],
      },
      {
        id: 'dailymotion',
        name: 'Dailymotion',
        description: 'Plateforme vidéo alternative avec contenus variés.',
        url: 'https://www.dailymotion.com',
        icon: 'https://cdn.simpleicons.org/dailymotion/0066DC?v=1763029785513',
        availability: ['global'],
        tags: ['Gratuit', 'Vidéos'],
      },
      {
        id: 'pluto-tv',
        name: 'Pluto TV',
        description: 'TV en direct et VOD gratuits avec publicités.',
        url: 'https://pluto.tv/live-tv',
        icon: 'https://cdn.simpleicons.org/pluto/000000?v=1763029785513',
        availability: ['global'],
        tags: ['Gratuit', 'Live TV'],
      },
      {
        id: 'tubi',
        name: 'Tubi',
        description: 'Films et séries gratuits financés par publicités.',
        url: 'https://tubitv.com',
        icon: 'https://cdn.simpleicons.org/tubi/FA3C10?v=1763029785513',
        availability: ['global'],
        tags: ['Gratuit', 'Films'],
      },
      {
        id: 'plex',
        name: 'Plex',
        description: 'Serveur média personnel et contenus gratuits.',
        url: 'https://app.plex.tv',
        icon: 'https://cdn.simpleicons.org/plex/EBAF00?v=1763029785513',
        availability: ['global'],
        tags: ['Media Server', 'Gratuit'],
      },
      {
        id: 'rakuten-viki',
        name: 'Viki',
        description: 'Dramas asiatiques avec sous-titres communautaires.',
        url: 'https://www.viki.com',
        icon: 'https://cdn.simpleicons.org/rakuten/BF0000?v=1763029785513',
        availability: ['global'],
        tags: ['K-Drama', 'Asie'],
      },
      {
        id: 'espn-plus',
        name: 'ESPN+',
        description: 'Sports en direct et contenus ESPN exclusifs.',
        url: 'https://plus.espn.com',
        icon: 'https://cdn.simpleicons.org/espn/D22730?v=1763029785513',
        availability: ['north-america'],
        tags: ['Sports', 'Live'],
      },
      {
        id: 'dazn',
        name: 'DAZN',
        description: 'Plateforme mondiale de sports en streaming.',
        url: 'https://www.dazn.com',
        icon: 'https://cdn.simpleicons.org/dazn/FBFF00?v=1763029785513',
        availability: ['global'],
        tags: ['Sports', 'Live'],
      },
      {
        id: 'roku-channel',
        name: 'The Roku Channel',
        description: 'Streaming gratuit avec films, séries et live TV.',
        url: 'https://therokuchannel.roku.com',
        icon: 'https://cdn.simpleicons.org/roku/6F1AB1?v=1763029785513',
        availability: ['north-america'],
        tags: ['Gratuit', 'Live TV'],
      },
      {
        id: 'xumo',
        name: 'Xumo',
        description: 'TV en direct gratuite avec 190+ chaînes.',
        url: 'https://www.xumo.tv',
        icon: 'https://cdn.simpleicons.org/x/000000?v=1763029785513',
        availability: ['north-america'],
        tags: ['Gratuit', 'Live'],
      },
      {
        id: 'stirr',
        name: 'Stirr',
        description: 'Actualités locales et divertissement en direct gratuit.',
        url: 'https://www.stirr.com',
        icon: 'https://cdn.simpleicons.org/starz/000000?v=1763029785513',
        availability: ['north-america'],
        tags: ['News', 'Gratuit'],
      },
      {
        id: 'freevee',
        name: 'Amazon Freevee',
        description: 'Films et séries originaux gratuits avec pub.',
        url: 'https://www.amazon.com/freevee',
        icon: 'https://cdn-icons-png.flaticon.com/512/3074/3074767.png',
        availability: ['global'],
        tags: ['Gratuit', 'Originals'],
      },
      {
        id: 'redbox',
        name: 'Redbox',
        description: 'Location et streaming gratuit de films.',
        url: 'https://www.redbox.com',
        icon: 'https://cdn.simpleicons.org/redhat/EE0000?v=1763029785513',
        availability: ['north-america'],
        tags: ['Location', 'Gratuit'],
      },
      {
        id: 'vudu',
        name: 'Vudu',
        description: 'Location, achat et contenus gratuits avec pub.',
        url: 'https://www.vudu.com',
        icon: 'https://cdn.simpleicons.org/v/4285F4?v=1763029785513',
        availability: ['north-america'],
        tags: ['VOD', 'Gratuit'],
      },
      {
        id: 'hoopla',
        name: 'Hoopla',
        description: 'Streaming gratuit via bibliothèques publiques.',
        url: 'https://www.hoopladigital.com',
        icon: 'https://cdn.simpleicons.org/hugo/FF4088?v=1763029785513',
        availability: ['north-america'],
        tags: ['Bibliothèque', 'Gratuit'],
      },
      {
        id: 'kanopy',
        name: 'Kanopy',
        description: 'Films d\'art et documentaires via bibliothèques.',
        url: 'https://www.kanopy.com',
        icon: 'https://cdn.simpleicons.org/k/0087D7?v=1763029785513',
        availability: ['global'],
        tags: ['Éducatif', 'Gratuit'],
      },
      {
        id: 'archive-org',
        name: 'Internet Archive',
        description: 'Films classiques et contenus du domaine public.',
        url: 'https://archive.org/details/movies',
        icon: 'https://cdn.simpleicons.org/internetarchive/000000?v=1763029785513',
        availability: ['global'],
        tags: ['Classique', 'Gratuit'],
      },
      {
        id: 'hulu',
        name: 'Hulu',
        description: 'Streaming US avec live TV et contenus exclusifs.',
        url: 'https://www.hulu.com',
        icon: 'https://cdn.simpleicons.org/hulu/1CE783?v=1763029785513',
        availability: ['north-america'],
        tags: ['Live TV', 'Originals'],
      },
      {
        id: 'showtime',
        name: 'Showtime',
        description: 'Séries premium et documentaires exclusifs.',
        url: 'https://www.showtime.com',
        icon: 'https://cdn.simpleicons.org/showtime/D81921?v=1763029785513',
        availability: ['north-america'],
        tags: ['Premium', 'Séries'],
      },
      {
        id: 'starz',
        name: 'Starz',
        description: 'Films blockbusters et séries originales.',
        url: 'https://www.starz.com',
        icon: 'https://cdn.simpleicons.org/starz/000000?v=1763029785513',
        availability: ['north-america'],
        tags: ['Premium', 'Films'],
      },
      {
        id: 'curiositystream',
        name: 'CuriosityStream',
        description: 'Documentaires scientifiques et nature.',
        url: 'https://curiositystream.com',
        icon: 'https://logo.clearbit.com/curiositystream.com',
        availability: ['global'],
        tags: ['Documentaire', 'Science'],
      },
      {
        id: 'xfinity-stream',
        name: '\uD83C\uDDFA\uD83C\uDDF8 Xfinity Stream',
        description: 'TV en direct et VOD pour abonnés Comcast Xfinity via navigateur.',
        url: 'https://www.xfinity.com/stream/',
        icon: 'https://logo.clearbit.com/xfinity.com',
        availability: ['north-america'],
        tags: ['USA', 'Opérateur', 'TV'],
      },
      {
        id: 'spectrum-tv',
        name: '\uD83C\uDDFA\uD83C\uDDF8 Spectrum TV',
        description: 'Live TV et replay en ligne pour clients Spectrum.',
        url: 'https://watch.spectrum.net/',
        icon: 'https://logo.clearbit.com/spectrum.net',
        availability: ['north-america'],
        tags: ['USA', 'Opérateur', 'TV'],
      },
      {
        id: 'directv-stream',
        name: '\uD83C\uDDFA\uD83C\uDDF8 DIRECTV STREAM',
        description: 'Service de streaming TV de DIRECTV avec chaînes live et VOD.',
        url: 'https://www.directv.com/stream/',
        icon: 'https://logo.clearbit.com/directv.com',
        availability: ['north-america'],
        tags: ['USA', 'OTT', 'TV'],
      },
      {
        id: 'youtube-tv',
        name: '\uD83C\uDDFA\uD83C\uDDF8 YouTube TV',
        description: 'Bouquet TV live de YouTube avec enregistrement dans le cloud.',
        url: 'https://tv.youtube.com/',
        icon: 'https://logo.clearbit.com/tv.youtube.com',
        availability: ['north-america'],
        tags: ['USA', 'OTT', 'TV'],
      },
      {
        id: 'sling-tv',
        name: '\uD83C\uDDFA\uD83C\uDDF8 Sling TV',
        description: 'Service OTT avec bouquets TV thématiques aux USA.',
        url: 'https://www.sling.com/',
        icon: 'https://logo.clearbit.com/sling.com',
        availability: ['north-america'],
        tags: ['USA', 'OTT', 'TV'],
      },
      {
        id: 'fubo-tv',
        name: '\uD83C\uDDFA\uD83C\uDDF8 fuboTV',
        description: 'Plateforme TV axée sport (foot, NFL, NBA...) avec web player.',
        url: 'https://www.fubo.tv/',
        icon: 'https://logo.clearbit.com/fubo.tv',
        availability: ['north-america'],
        tags: ['USA', 'Sport', 'TV'],
      },
    ],
  },
  {
    id: 'free-tv',
    title: 'TV gratuite & Live',
    subtitle: 'Chaînes en direct et contenus gratuits sans abonnement',
    colorFrom: 'from-orange-500',
    colorTo: 'to-red-500',
    platforms: [
      {
        id: 'livestream',
        name: 'Livestream',
        description: 'Diffusions live et événements en direct.',
        url: 'https://livestream.com',
        icon: 'https://cdn.simpleicons.org/livestream/CF3A2C?v=1763029785513',
        availability: ['global'],
        tags: ['Live', 'Événements'],
      },
      {
        id: 'molotov',
        name: 'Molotov TV',
        description: 'TV française en direct et replay gratuit.',
        url: 'https://www.molotov.tv',
        icon: '/icons/molotov.svg',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['France', 'Gratuit'],
      },
      {
        id: 'free-tv-plus',
        name: 'Free TV+',
        description: 'Service de streaming TV gratuit de Free avec replay et direct.',
        url: 'https://tv.free.fr',
        icon: '/icons/free.svg',
        availability: ['europe'],
        tags: ['France', 'Free', 'Gratuit'],
      },
      {
        id: 'orange-tv',
        name: "TV d'Orange",
        description: 'TV en direct et replay pour abonnés Orange.',
        url: 'https://chaines-tv.orange.fr',
        icon: '/icons/orange.svg',
        availability: ['france', 'europe'],
        tags: ['France', 'Orange', 'TV'],
      },
      {
        id: 'sfr-tv',
        name: 'SFR TV sur PC',
        description: 'TV en direct, replay et VOD pour abonnés SFR sur ordinateur.',
        url: 'https://tv.sfr.fr',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/2/2e/SFR_logo_2014.svg?v=1763029785513',
        availability: ['france', 'europe'],
        tags: ['France', 'SFR', 'TV'],
      },
      {
        id: 'bouygues-btv',
        name: 'B.tv Bouygues',
        description: 'TV en direct et replay Bouygues Telecom depuis le navigateur.',
        url: 'https://www.bouyguestelecom.fr/tv-direct/',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/0/02/Bouygues_Telecom_logo.svg?v=1763029785513',
        availability: ['france', 'europe'],
        tags: ['France', 'Bouygues', 'TV'],
      },
      {
        id: 'tvmucho',
        name: 'TVMucho',
        description: 'TV européenne en streaming gratuit.',
        url: 'https://www.tvmucho.com',
        icon: 'https://cdn.simpleicons.org/twitchtv/9146FF?v=1763029785513',
        availability: ['global', 'europe'],
        tags: ['Europe', 'Gratuit'],
      },
      {
        id: 'arte',
        name: 'Arte',
        description: 'Culture, documentaires et cinéma d\'auteur FR/DE.',
        url: 'https://www.arte.tv/fr/direct/',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/4/4b/Arte_Logo_2017.svg?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Culture', 'Gratuit', 'FR/DE'],
      },
      {
        id: 'francetv',
        name: 'France.tv',
        description: 'TV publique française avec replay gratuit.',
        url: 'https://www.france.tv/direct',
        icon: 'https://upload.wikimedia.org/wikipedia/fr/3/3b/France_T%C3%A9l%C3%A9visions_2018.svg?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Gratuit', 'France'],
      },
      {
        id: 'zdf',
        name: 'ZDF Mediathek',
        description: 'TV publique allemande avec contenus gratuits.',
        url: 'https://www.zdf.de/live-tv',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/ZDF_logo.svg?v=1763029785513',
        availability: ['global', 'europe', 'northern_europe', 'germany'],
        tags: ['Gratuit', 'Deutschland'],
      },
    ],
  },
  {
    id: 'europe',
    title: 'Sélection Europe',
    subtitle: 'Les services les plus appréciés en Europe',
    colorFrom: 'from-blue-600',
    colorTo: 'to-indigo-500',
    platforms: [
      {
        id: 'francetv-europe',
        name: 'France.tv (Europe)',
        description: 'Replay et direct de l’audiovisuel public français.',
        url: 'https://www.france.tv',
        icon: 'https://upload.wikimedia.org/wikipedia/fr/3/3b/France_T%C3%A9l%C3%A9visions_2018.svg?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Replay', 'Direct', 'France'],
      },
      {
        id: 'bbc-iplayer',
        name: 'BBC iPlayer',
        description: 'Programmes BBC avec interface cinématographique.',
        url: 'https://www.bbc.co.uk/iplayer',
        icon: 'https://cdn.simpleicons.org/bbc/000000?v=1763029785513',
        availability: ['global', 'europe', 'anglophone', 'uk'],
        tags: ['Live', 'News'],
      },
      {
        id: 'ard-mediathek',
        name: 'ARD Mediathek',
        description: 'Plateforme allemande avec contenus culturels.',
        url: 'https://www.ardmediathek.de/live',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/5/5c/ARD_logo.svg?v=1763029785513',
        availability: ['global', 'europe', 'northern_europe', 'germany'],
      },
      {
        id: 'canalplus',
        name: 'Canal+ (MyCanal)',
        description: 'Cinéma, séries, sport et divertissement premium.',
        url: 'https://www.canalplus.com',
        icon: 'https://cdn-icons-png.flaticon.com/512/2809/2809590.png',
        availability: ['europe'],
        tags: ['Premium', 'Sport'],
      },
      {
        id: '6play',
        name: '6Play (M6+)',
        description: 'Replay des chaînes du groupe M6 en direct et replay.',
        url: 'https://www.6play.fr/',
        icon: 'https://upload.wikimedia.org/wikipedia/fr/e/e8/6play_logo_2018.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['Gratuit', 'Replay'],
      },
      {
        id: 'tf1plus',
        name: 'TF1+',
        description: 'Programmes TF1 en direct et replay gratuit.',
        url: 'https://www.tf1.fr/',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/d/da/TF1_logo_2013.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['Gratuit', 'Direct'],
      },
      {
        id: 'bfmtv',
        name: 'BFM TV',
        description: 'Info en continu en direct.',
        url: 'https://www.bfmtv.com/mediaplayer/live-video/',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/3/3c/BFMTV_logo_%282019%29.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['News', 'Live'],
      },
      {
        id: 'lci',
        name: 'LCI',
        description: 'Chaîne d\'info du groupe TF1 en direct.',
        url: 'https://www.lci.fr/direct/',
        icon: 'https://upload.wikimedia.org/wikipedia/fr/d/de/Logo_LCI_2016.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['News', 'Live'],
      },
      {
        id: 'cnews',
        name: 'CNews',
        description: 'Info en continu en direct.',
        url: 'https://www.cnews.fr/le-direct',
        icon: 'https://upload.wikimedia.org/wikipedia/fr/2/20/CNews_2017.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['News', 'Live'],
      },
      {
        id: 'rtbf',
        name: 'RTBF Auvio',
        description: 'TV publique belge francophone avec replay.',
        url: 'https://auvio.rtbf.be/',
        icon: 'https://upload.wikimedia.org/wikipedia/commons/3/3c/RTBF_logo.svg?v=1763029785513',
        availability: ['europe'],
        tags: ['Belgique', 'Gratuit'],
      },
      {
        id: 'rtlplay',
        name: 'RTL Play',
        description: 'Streaming belge RTL avec séries, films et direct.',
        url: 'https://www.rtlplay.be/',
        icon: 'https://cdn.simpleicons.org/rtl/0084C9?v=1763029785513',
        availability: ['europe'],
        tags: ['Belgique', 'Gratuit'],
      },
      {
        id: 'pickx',
        name: 'Pickx',
        description: 'Streaming TV belge avec de nombreuses chaînes.',
        url: 'https://www.pickx.be/fr',
        icon: 'https://cdn.simpleicons.org/plex/EBAF00?v=1763029785513',
        availability: ['europe'],
        tags: ['Belgique'],
      },
      {
        id: 'rtsplay',
        name: 'RTS Play',
        description: 'TV publique suisse romande avec replay.',
        url: 'https://www.rts.ch/play/tv',
        icon: 'https://cdn-icons-png.flaticon.com/512/197/197540.png',
        availability: ['europe'],
        tags: ['Suisse', 'Gratuit'],
      },
      {
        id: 'playsuisse',
        name: 'Play Suisse',
        description: 'Streaming suisse avec contenus locaux.',
        url: 'https://www.playsuisse.ch/fr',
        icon: 'https://cdn.simpleicons.org/playstation/003791?v=1763029785513',
        availability: ['europe'],
        tags: ['Suisse'],
      },
      {
        id: 'dazn-europe',
        name: 'DAZN France',
        description: 'Streaming sport en direct et à la demande.',
        url: 'https://www.dazn.com/fr-FR',
        icon: 'https://logo.clearbit.com/dazn.com',
        availability: ['europe'],
        tags: ['Sport', 'Live'],
      },
      {
        id: 'justwatch',
        name: 'JustWatch',
        description: 'Trouve où regarder films et séries en streaming.',
        url: 'https://www.justwatch.com/fr',
        icon: 'https://logo.clearbit.com/justwatch.com',
        availability: ['global'],
        tags: ['Agrégateur', 'Utile'],
      },
      {
        id: 'magenta-tv',
        name: '\uD83C\uDDE9\uD83C\uDDEA MagentaTV',
        description: 'TV en direct et replay de Deutsche Telekom, accessible sur navigateur.',
        url: 'https://web.magentatv.de/',
        icon: 'https://logo.clearbit.com/magentatv.de',
        availability: ['europe', 'germany'],
        tags: ['Allemagne', 'Opérateur', 'TV'],
      },
      {
        id: 'waipu-tv',
        name: '\uD83C\uDDE9\uD83C\uDDEA waipu.tv',
        description: 'TV IP et replay en Allemagne, 100% via Internet.',
        url: 'https://www.waipu.tv/',
        icon: 'https://logo.clearbit.com/waipu.tv',
        availability: ['europe', 'germany'],
        tags: ['Allemagne', 'IPTV', 'TV'],
      },
      {
        id: 'zattoo-eu',
        name: '\uD83C\uDDE9\uD83C\uDDEA\uD83C\uDDE8\uD83C\uDDED Zattoo',
        description: 'Plateforme pan-europ\u00e9enne de TV en direct sur navigateur.',
        url: 'https://zattoo.com/de/en',
        icon: 'https://logo.clearbit.com/zattoo.com',
        availability: ['europe'],
        tags: ['IPTV', 'Live', 'Europe'],
      },
      {
        id: 'joyn-de',
        name: '\uD83C\uDDE9\uD83C\uDDEA Joyn',
        description: 'Streaming et live TV en Allemagne (ProSiebenSat.1).',
        url: 'https://www.joyn.de/',
        icon: 'https://logo.clearbit.com/joyn.de',
        availability: ['europe', 'germany'],
        tags: ['Allemagne', 'Live', 'Replay'],
      },
      {
        id: 'vodafone-gigatv-de',
        name: '\uD83C\uDDE9\uD83C\uDDEA Vodafone GigaTV',
        description: 'TV en direct et replay Vodafone, accessible aussi via navigateur.',
        url: 'https://www.vodafone.de/digital-fernsehen/online-tv',
        icon: 'https://logo.clearbit.com/vodafone.de',
        availability: ['europe', 'germany'],
        tags: ['Allemagne', 'Opérateur', 'TV'],
      },
      {
        id: 'movistar-plus-es',
        name: '\uD83C\uDDEA\uD83C\uDDF8 Movistar Plus+',
        description: 'Plateforme TV payante de Telef\u00f3nica (Espagne) avec web player.',
        url: 'https://ver.movistarplus.es/',
        icon: 'https://logo.clearbit.com/movistarplus.es',
        availability: ['europe', 'spain'],
        tags: ['Espagne', 'Opérateur', 'TV'],
      },
      {
        id: 'vodafone-tv-es',
        name: '\uD83C\uDDEA\uD83C\uDDF8 Vodafone TV Espa\u00f1a',
        description: 'TV en ligne de Vodafone Espagne, live + replay.',
        url: 'https://tv.vodafone.es/',
        icon: 'https://logo.clearbit.com/vodafone.es',
        availability: ['europe', 'spain'],
        tags: ['Espagne', 'Opérateur', 'TV'],
      },
      {
        id: 'orange-tv-es',
        name: '\uD83C\uDDEA\uD83C\uDDF8 Orange TV Espa\u00f1a',
        description: 'TV et VOD d\'Orange Espagne, accessible sur PC et mobile.',
        url: 'https://orangetv.orange.es/',
        icon: 'https://logo.clearbit.com/orange.es',
        availability: ['europe', 'spain'],
        tags: ['Espagne', 'Orange', 'TV'],
      },
      {
        id: 'atresplayer',
        name: '\uD83C\uDDEA\uD83C\uDDF8 Atresplayer',
        description: 'Plateforme de streaming d\'Atresmedia (Antena 3, La Sexta...).',
        url: 'https://www.atresplayer.com/',
        icon: 'https://logo.clearbit.com/atresplayer.com',
        availability: ['europe', 'spain'],
        tags: ['Espagne', 'Replay'],
      },
      {
        id: 'mitele-es',
        name: '\uD83C\uDDEA\uD83C\uDDF8 Mitele / Mediaset Infinity ES',
        description: 'Plateforme de Mediaset Espa\u00f1a avec live et replay.',
        url: 'https://www.mitele.es/',
        icon: 'https://logo.clearbit.com/mitele.es',
        availability: ['europe', 'spain'],
        tags: ['Espagne', 'Replay'],
      },
      {
        id: 'timvision',
        name: '\uD83C\uDDEE\uD83C\uDDF9 TIMVISION',
        description: 'Service de streaming de l\'opérateur TIM en Italie.',
        url: 'https://www.timvision.it/',
        icon: 'https://logo.clearbit.com/timvision.it',
        availability: ['europe', 'italy'],
        tags: ['Italie', 'Opérateur', 'TV'],
      },
      {
        id: 'raiplay',
        name: '\uD83C\uDDEE\uD83C\uDDF9 RaiPlay',
        description: 'Plateforme de streaming gratuite de la Rai (Italie).',
        url: 'https://www.raiplay.it/',
        icon: 'https://logo.clearbit.com/raiplay.it',
        availability: ['europe', 'italy'],
        tags: ['Italie', 'Public', 'Gratuit'],
      },
      {
        id: 'mediaset-infinity-it',
        name: '\uD83C\uDDEE\uD83C\uDDF9 Mediaset Infinity',
        description: 'Plateforme streaming de Mediaset en Italie.',
        url: 'https://mediasetinfinity.mediaset.it/',
        icon: 'https://logo.clearbit.com/mediasetinfinity.mediaset.it',
        availability: ['europe', 'italy'],
        tags: ['Italie', 'VOD', 'Replay'],
      },
      {
        id: 'sky-go-uk',
        name: '\uD83C\uDDEC\uD83C\uDDE7 Sky Go',
        description: 'Service de streaming TV de Sky pour abonnés au Royaume-Uni et en Irlande.',
        url: 'https://www.sky.com/watch/sky-go',
        icon: 'https://logo.clearbit.com/sky.com',
        availability: ['europe', 'uk'],
        tags: ['UK', 'Opérateur', 'TV'],
      },
      {
        id: 'now-tv-uk',
        name: '\uD83C\uDDEC\uD83C\uDDE7 NOW (Sky)',
        description: 'Service OTT de Sky sans engagement (s\u00e9ries, cin\u00e9, sport).',
        url: 'https://www.nowtv.com/',
        icon: 'https://logo.clearbit.com/nowtv.com',
        availability: ['europe', 'uk'],
        tags: ['UK', 'OTT', 'VOD'],
      },
      {
        id: 'virgin-tv-go-uk',
        name: '\uD83C\uDDEC\uD83C\uDDE7 Virgin TV Go',
        description: 'TV et VOD en ligne pour clients Virgin Media.',
        url: 'https://virgintvgo.virginmedia.com/en.html',
        icon: 'https://logo.clearbit.com/virginmedia.com',
        availability: ['europe', 'uk'],
        tags: ['UK', 'Opérateur', 'TV'],
      },
      {
        id: 'adn',
        name: 'ADN (Anime Digital Network)',
        description: 'Plateforme française leader de l\'anime légal en streaming.',
        url: 'https://animedigitalnetwork.fr',
        icon: 'https://cdn.simpleicons.org/anime/FF0000?v=1763029785513',
        availability: ['europe'],
        tags: ['Anime', 'France', 'Premium'],
      },
      {
        id: 'wakanim',
        name: 'Wakanim',
        description: 'Anime en streaming légal pour l\'Europe francophone.',
        url: 'https://www.wakanim.tv',
        icon: 'https://cdn.simpleicons.org/w3c/005A9C?v=1763029785513',
        availability: ['europe'],
        tags: ['Anime', 'France', 'VF/VOSTFR'],
      },
      {
        id: 'npo-start',
        name: '\uD83C\uDDF3\uD83C\uDDF1 NPO Start',
        description: 'Plateforme de rattrapage de la télévision publique néerlandaise.',
        url: 'https://www.npostart.nl/',
        icon: 'https://logo.clearbit.com/npo.nl',
        availability: ['europe', 'northern_europe'],
        tags: ['Pays-Bas', 'Public', 'Replay'],
      },
      {
        id: 'nlziet',
        name: '\uD83C\uDDF3\uD83C\uDDF1 NLZIET',
        description: 'Service OTT néerlandais regroupant NPO, RTL et SBS.',
        url: 'https://www.nlziet.nl/',
        icon: 'https://logo.clearbit.com/nlziet.nl',
        availability: ['europe', 'northern_europe'],
        tags: ['Pays-Bas', 'OTT', 'TV'],
      },
      {
        id: 'svt-play',
        name: '\uD83C\uDDF8\uD83C\uDDEA SVT Play',
        description: 'Service de streaming de la télévision publique suédoise.',
        url: 'https://www.svtplay.se/',
        icon: 'https://logo.clearbit.com/svtplay.se',
        availability: ['europe', 'northern_europe'],
        tags: ['Suède', 'Public', 'Gratuit'],
      },
      {
        id: 'dr-tv',
        name: '\uD83C\uDDE9\uD83C\uDDF0 DR TV',
        description: 'Streaming de la télévision publique danoise (DR).',
        url: 'https://www.dr.dk/drtv/',
        icon: 'https://logo.clearbit.com/dr.dk',
        availability: ['europe', 'northern_europe'],
        tags: ['Danemark', 'Public', 'Gratuit'],
      },
      {
        id: 'nrk-tv',
        name: '\uD83C\uDDF3\uD83C\uDDF4 NRK TV',
        description: 'Plateforme de streaming de la télévision publique norvégienne.',
        url: 'https://tv.nrk.no/',
        icon: 'https://logo.clearbit.com/nrk.no',
        availability: ['europe', 'northern_europe'],
        tags: ['Norvège', 'Public', 'Gratuit'],
      },
      {
        id: 'yle-areena',
        name: '\uD83C\uDDEB\uD83C\uDDEE Yle Areena',
        description: 'Service de streaming de la télévision publique finlandaise.',
        url: 'https://areena.yle.fi/',
        icon: 'https://logo.clearbit.com/yle.fi',
        availability: ['europe', 'northern_europe'],
        tags: ['Finlande', 'Public', 'Gratuit'],
      },
      {
        id: 'stan',
        name: 'Stan',
        description: 'Australian streaming service with local and international content.',
        url: 'https://www.stan.com.au',
        icon: 'https://logo.clearbit.com/stan.com.au',
        availability: ['australia'],
        tags: ['Australia', 'Streaming', 'Local'],
      },
      {
        id: 'abc-iview',
        name: 'ABC iview',
        description: 'Free Australian public broadcaster streaming service.',
        url: 'https://iview.abc.net.au',
        icon: 'https://cdn.simpleicons.org/abc/000000?v=1763029785513',
        availability: ['australia'],
        tags: ['Australia', 'Free', 'Public'],
      },
      {
        id: 'sbs-on-demand',
        name: 'SBS On Demand',
        description: 'Free multicultural streaming from Australian broadcaster.',
        url: 'https://www.sbs.com.au/ondemand',
        icon: 'https://cdn.simpleicons.org/s/FF0000?v=1763029785513',
        availability: ['australia'],
        tags: ['Australia', 'Free', 'Multicultural'],
      },
      {
        id: '7plus',
        name: '7plus',
        description: 'Free streaming from Seven Network Australia.',
        url: 'https://7plus.com.au',
        icon: 'https://cdn.simpleicons.org/7zip/00599C?v=1763029785513',
        availability: ['australia'],
        tags: ['Australia', 'Free', 'TV'],
      },
      {
        id: '9now',
        name: '9Now',
        description: 'Free streaming from Nine Network Australia.',
        url: 'https://www.9now.com.au',
        icon: 'https://cdn.simpleicons.org/9gag/000000?v=1763029785513',
        availability: ['australia'],
        tags: ['Australia', 'Free', 'TV'],
      },
      {
        id: '10play',
        name: '10 play',
        description: 'Free streaming from Network 10 Australia.',
        url: 'https://10play.com.au',
        icon: 'https://logo.clearbit.com/network10.com.au',
        availability: ['australia'],
        tags: ['Australia', 'Free', 'TV'],
      },
      {
        id: 'kayo-sports',
        name: 'Kayo Sports',
        description: 'Australian sports streaming service with live events.',
        url: 'https://kayosports.com.au',
        icon: 'https://logo.clearbit.com/kayosports.com.au',
        availability: ['australia'],
        tags: ['Australia', 'Sport', 'Live'],
      },
      {
        id: 'emby',
        name: 'Emby',
        description: 'Media server personnel pour vos films et séries.',
        url: 'https://app.emby.media',
        icon: 'https://cdn.simpleicons.org/emby/52B54B?v=1763029785513',
        availability: ['global'],
        tags: ['Media Server'],
      },
      {
        id: 'jellyfin',
        name: 'Jellyfin',
        description: 'Media server open-source gratuit.',
        url: 'https://jellyfin.org/downloads/clients/#web',
        icon: 'https://cdn.simpleicons.org/jellyfin/00A4DC?v=1763029785513',
        availability: ['global'],
        tags: ['Media Server', 'Open Source'],
      },
    ],
  },
  {
    id: 'asia-streaming',
    title: 'Streaming Asie & Chine',
    subtitle: 'Contenus premium pour conducteurs XPENG en Asie (masqué hors Asie)',
    highlight: 'Optimisé pour Xmart OS',
    colorFrom: 'from-emerald-500',
    colorTo: 'to-lime-400',
    platforms: [
      {
        id: 'bilibili',
        name: 'Bilibili',
        description: 'Le meilleur de l’animation et du e-sport chinois.',
        url: 'https://www.bilibili.com',
        icon: 'https://cdn.simpleicons.org/bilibili/00A1D6?v=1763029785513',
        availability: ['china', 'asia'],
        tags: ['Anime', 'Live'],
      },
      {
        id: 'iqiyi',
        name: 'iQIYI',
        description: 'Séries, cinéma et variétés chinoises.',
        url: 'https://www.iq.com',
        icon: 'https://cdn.simpleicons.org/iqiyi/00CC8E?v=1763029785513',
        availability: ['china'],
        tags: ['HDR'],
      },
      {
        id: 'tencent-video',
        name: 'Tencent Video',
        description: 'Blockbusters et drama populaires asiatiques.',
        url: 'https://v.qq.com',
        icon: 'https://cdn.simpleicons.org/tencentqq/EB1923?v=1763029785513',
        availability: ['china', 'asia'],
      },
      {
        id: 'youku',
        name: 'Youku',
        description: 'Streaming vidéo premium Alibaba avec dramas et films.',
        url: 'https://www.youku.com',
        icon: 'https://cdn.simpleicons.org/youtube/FF0000?v=1763029785513',
        availability: ['china', 'asia'],
        tags: ['Alibaba', 'Drama'],
      },
      {
        id: 'douyin',
        name: 'Douyin 抖音',
        description: 'Vidéos courtes populaires en Chine (TikTok chinois).',
        url: 'https://www.douyin.com',
        icon: 'https://cdn.simpleicons.org/tiktok/000000?v=1763029785513',
        availability: ['china'],
        tags: ['Viral', 'Short Video'],
      },
      {
        id: 'mango-tv',
        name: 'Mango TV',
        description: 'Variétés et reality shows chinois populaires.',
        url: 'https://www.mgtv.com',
        icon: 'https://cdn.simpleicons.org/mantine/339AF0?v=1763029785513',
        availability: ['china', 'asia'],
        tags: ['Reality', 'Variétés'],
      },
    ],
  },
  {
    id: 'learning',
    title: 'Éducation & Apprentissage',
    subtitle: 'Cours en ligne et contenus éducatifs',
    colorFrom: 'from-indigo-500',
    colorTo: 'to-blue-500',
    platforms: [
      {
        id: 'ted',
        name: 'TED Talks',
        description: 'Conférences inspirantes d\'experts mondiaux.',
        url: 'https://www.ted.com',
        icon: 'https://cdn-icons-png.flaticon.com/512/3670/3670151.png',
        availability: ['global'],
        tags: ['Éducatif', 'Gratuit'],
      },
      {
        id: 'khan-academy',
        name: 'Khan Academy',
        description: 'Éducation gratuite pour tous, partout.',
        url: 'https://www.khanacademy.org',
        icon: 'https://cdn.simpleicons.org/khanacademy/14BF96?v=1763029785513',
        availability: ['global'],
        tags: ['Gratuit', 'Éducation'],
      },
      {
        id: 'coursera',
        name: 'Coursera',
        description: 'Cours universitaires en ligne du monde entier.',
        url: 'https://www.coursera.org',
        icon: 'https://cdn.simpleicons.org/coursera/0056D2?v=1763029785513',
        availability: ['global'],
        tags: ['Université', 'Certificats'],
      },
      {
        id: 'udemy',
        name: 'Udemy',
        description: 'Plateforme de cours en ligne variés.',
        url: 'https://www.udemy.com',
        icon: 'https://cdn.simpleicons.org/udemy/A435F0?v=1763029785513',
        availability: ['global'],
        tags: ['Compétences', 'Payant'],
      },
      {
        id: 'skillshare',
        name: 'Skillshare',
        description: 'Apprenez des compétences créatives en ligne.',
        url: 'https://www.skillshare.com',
        icon: 'https://cdn.simpleicons.org/skillshare/00FF84?v=1763029785513',
        availability: ['global'],
        tags: ['Créatif', 'Abonnement'],
      },
      {
        id: 'masterclass',
        name: 'MasterClass',
        description: 'Apprenez des meilleurs experts mondiaux.',
        url: 'https://www.masterclass.com',
        icon: 'https://cdn.simpleicons.org/mastercard/EB001B?v=1763029785513',
        availability: ['global'],
        tags: ['Premium', 'Experts'],
      },
      {
        id: 'edx',
        name: 'edX',
        description: 'Cours des meilleures universités gratuitement.',
        url: 'https://www.edx.org',
        icon: 'https://cdn.simpleicons.org/edx/02262B?v=1763029785513',
        availability: ['global'],
        tags: ['MIT', 'Harvard'],
      },
      {
        id: 'linkedin-learning',
        name: 'LinkedIn Learning',
        description: 'Développement professionnel et compétences.',
        url: 'https://www.linkedin.com/learning',
        icon: 'https://cdn.simpleicons.org/linkedin/0A66C2?v=1763029785513',
        availability: ['global'],
        tags: ['Pro', 'Business'],
      },
    ],
  },
];

export const musicCategories: PlatformCategory[] = [
  {
    id: 'music',
    title: 'Musique & Audio',
    subtitle: 'Streaming musical XPENG Sound + radios par pays (BBC, Radio France, Radioplayer...)',
    highlight: 'Audio Spatial',
    colorFrom: 'from-purple-600',
    colorTo: 'to-pink-500',
    platforms: [
      {
        id: 'spotify',
        name: 'Spotify',
        description: 'Playlists adaptées à vos trajets XPENG.',
        url: 'https://www.spotify.com',
        icon: '/icons/spotify.svg',
        availability: ['global'],
        tags: ['Car Mode'],
      },
      {
        id: 'apple-music',
        name: 'Apple Music',
        description: 'Audio spatial et Lossless.',
        url: 'https://music.apple.com',
        icon: 'https://cdn.simpleicons.org/applemusic/FA243C?v=1763029785513',
        availability: ['global'],
        tags: ['Hi-Res'],
        isPremium: true,
      },
      {
        id: 'deezer',
        name: 'Deezer',
        description: 'Flow personnalisé pour la route.',
        url: 'https://www.deezer.com',
        icon: 'https://cdn.simpleicons.org/deezer/FEAA2D?v=1763029785513',
        availability: ['europe'],
      },
      {
        id: 'youtube-music',
        name: 'YouTube Music',
        description: 'Millions de titres avec clips vidéo intégrés.',
        url: 'https://music.youtube.com',
        icon: '/icons/youtube-music.svg',
        availability: ['global'],
        tags: ['Vidéos', 'Gratuit'],
      },
      {
        id: 'tidal',
        name: 'Tidal',
        description: 'Streaming Hi-Fi et Master Quality Audio.',
        url: 'https://tidal.com',
        icon: 'https://cdn.simpleicons.org/tidal/000000?v=1763029785513',
        availability: ['global'],
        tags: ['Hi-Res', 'Lossless'],
      },
      {
        id: 'soundcloud',
        name: 'SoundCloud',
        description: 'Découvrez nouveaux artistes et remixes.',
        url: 'https://soundcloud.com',
        icon: 'https://cdn.simpleicons.org/soundcloud/FF3300?v=1763029785513',
        availability: ['global'],
        tags: ['Indie', 'Remixes'],
      },
      {
        id: 'amazon-music',
        name: 'Amazon Music',
        description: 'Streaming HD et Ultra HD inclus avec Prime.',
        url: 'https://music.amazon.com',
        icon: 'https://cdn.simpleicons.org/amazonmusic/1DB954?v=1763029785513',
        availability: ['global'],
        tags: ['HD', 'Prime'],
      },
      {
        id: 'pandora',
        name: 'Pandora',
        description: 'Radio personnalisée et découverte musicale.',
        url: 'https://www.pandora.com',
        icon: 'https://cdn.simpleicons.org/pandora/005483?v=1763029785513',
        availability: ['north-america'],
        tags: ['Radio', 'Gratuit'],
      },
      {
        id: 'audiomack',
        name: 'Audiomack',
        description: 'Streaming et découverte de nouveaux artistes hip-hop.',
        url: 'https://audiomack.com',
        icon: 'https://cdn.simpleicons.org/audiomack/FFA200?v=1763029785513',
        availability: ['global'],
        tags: ['Hip-Hop', 'Gratuit'],
      },
      {
        id: 'bandcamp',
        name: 'Bandcamp',
        description: 'Soutenez les artistes indépendants directement.',
        url: 'https://bandcamp.com',
        icon: 'https://cdn.simpleicons.org/bandcamp/1DA0C3?v=1763029785513',
        availability: ['global'],
        tags: ['Indie', 'Achat'],
      },
      {
        id: 'mixcloud',
        name: 'Mixcloud',
        description: 'DJ sets, podcasts et émissions radio.',
        url: 'https://www.mixcloud.com',
        icon: 'https://cdn.simpleicons.org/mixcloud/314359?v=1763029785513',
        availability: ['global'],
        tags: ['DJ', 'Radio'],
      },
      {
        id: 'iheartradio',
        name: 'iHeartRadio',
        description: 'Radio en direct et podcasts gratuits.',
        url: 'https://www.iheart.com',
        icon: 'https://cdn.simpleicons.org/iheart/C6002B?v=1763029785513',
        availability: ['north-america'],
        tags: ['Radio', 'Podcasts'],
      },
      {
        id: 'lastfm',
        name: 'Last.fm',
        description: 'Découverte musicale et scrobbling.',
        url: 'https://www.last.fm',
        icon: 'https://cdn.simpleicons.org/lastdotfm/D51007?v=1763029785513',
        availability: ['global'],
        tags: ['Découverte', 'Stats'],
      },
      {
        id: 'tunein',
        name: 'TuneIn',
        description: 'Radio mondiale et stations locales.',
        url: 'https://tunein.com',
        icon: 'https://cdn.simpleicons.org/tunein/1C203C?v=1763029785513',
        availability: ['global'],
        tags: ['Radio', 'Live'],
      },
      {
        id: 'bbc-sounds',
        name: '\uD83C\uDDEC\uD83C\uDDE7 BBC Sounds',
        description: 'Radios, podcasts et mix de la BBC.',
        url: 'https://www.bbc.co.uk/sounds',
        icon: '/icons/bbc-sounds.svg',
        availability: ['europe', 'anglophone', 'uk'],
        tags: ['Radio', 'Podcasts'],
      },
      {
        id: 'radio-france',
        name: '\uD83C\uDDEB\uD83C\uDDF7 Radio France',
        description: 'Accès web aux principales stations Radio France (France Inter, France Info...).',
        url: 'https://www.radiofrance.fr/',
        icon: '/icons/radio-france.svg',
        availability: ['europe', 'france'],
        tags: ['Radio', 'France', 'Public'],
      },
      {
        id: 'radioplayer-eu',
        name: '\uD83C\uDDEA\uD83C\uDDFA Radioplayer Europe',
        description: 'Plateforme européenne unifiée pour écouter les radios locales en ligne.',
        url: 'https://www.radioplayer.com/',
        icon: '/icons/radioplayer.svg',
        availability: ['europe'],
        tags: ['Radio', 'Agrégateur'],
      },
      {
        id: 'anghami',
        name: 'Anghami',
        description: 'Streaming musical du Moyen-Orient.',
        url: 'https://www.anghami.com',
        icon: 'https://cdn.simpleicons.org/anghami/A6005B?v=1763029785513',
        availability: ['middle-east'],
        tags: ['Arabe', 'Streaming'],
      },
    ],
  },
  {
    id: 'asia-music',
    title: 'Musique Asie & Chine',
    subtitle: 'Services intégrés à Xmart OS (masqué hors Asie)',
    colorFrom: 'from-teal-500',
    colorTo: 'to-sky-500',
    platforms: [
      {
        id: 'qq-music',
        name: 'QQ Music',
        description: 'Immense catalogue asiatique et karaoke embarqué.',
        url: 'https://y.qq.com',
        icon: 'https://cdn.simpleicons.org/tencentqq/EB1923?v=1763029785513',
        availability: ['china', 'asia'],
        tags: ['Lyrics'],
      },
      {
        id: 'netease',
        name: 'NetEase Cloud Music',
        description: 'Communauté musicale chinoise, podcasts et live.',
        url: 'https://music.163.com',
        icon: 'https://cdn-icons-png.flaticon.com/512/414/414927.png',
        availability: ['china'],
      },
    ],
  },
];

export const gamesCategories: PlatformCategory[] = [
  {
    id: 'gaming',
    title: 'Jeux & Divertissement',
    subtitle: 'Cloud gaming, jeux instantanés et divertissement familial',
    colorFrom: 'from-orange-500',
    colorTo: 'to-amber-400',
    platforms: [
      {
        id: 'geforce-now',
        name: 'GeForce NOW',
        description: 'Cloud gaming NVIDIA accessible depuis votre navigateur.',
        url: 'https://play.geforcenow.com',
        icon: 'https://cdn.simpleicons.org/nvidia/76B900?v=1763029785513',
        availability: ['global'],
        tags: ['Cloud'],
      },
      {
        id: 'boosteroid',
        name: 'Boosteroid',
        description: 'Cloud gaming européen accessible via navigateur web.',
        url: 'https://cloud.boosteroid.com',
        icon: 'https://cdn.simpleicons.org/boost/F7941D?v=1763029785513',
        availability: ['europe'],
        tags: ['Cloud Gaming'],
      },
      {
        id: 'xbox-cloud',
        name: 'Xbox Cloud Gaming',
        description: 'Xbox Cloud Gaming - Jouez via navigateur avec Game Pass Ultimate.',
        url: 'https://www.xbox.com/play',
        icon: 'https://cdn.simpleicons.org/xbox/107C10?v=1763029785513',
        availability: ['global'],
        tags: ['Game Pass', 'Cloud'],
      },
      {
        id: 'parsec',
        name: 'Parsec',
        description: 'Streaming gaming à faible latence depuis votre PC.',
        url: 'https://web.parsec.app/',
        icon: 'https://cdn.simpleicons.org/parsedotly/14A2FF?v=1763029785513',
        availability: ['global'],
        tags: ['Remote Gaming', 'Low Latency'],
      },
      {
        id: 'afterplay',
        name: 'Afterplay',
        description: 'Cloud gaming avec jeux AAA en streaming.',
        url: 'https://afterplay.io/',
        icon: 'https://cdn.simpleicons.org/afterpay/B2FCE4?v=1763029785513',
        availability: ['global'],
        tags: ['Cloud', 'AAA'],
      },
      {
        id: 'crazy-games',
        name: 'Crazy Games',
        description: 'Jeux HTML5 instantanés dans le navigateur.',
        url: 'https://www.crazygames.com',
        icon: 'https://cdn.simpleicons.org/crazyflie/222222?v=1763029785513',
        availability: ['global'],
        tags: ['Navigateur', 'Gratuit'],
      },
      {
        id: 'poki',
        name: 'Poki',
        description: 'Meilleurs jeux en ligne gratuits sans téléchargement.',
        url: 'https://poki.com',
        icon: 'https://cdn.simpleicons.org/pokemon/FFCB05?v=1763029785513',
        availability: ['global'],
        tags: ['Gratuit', 'Instantané'],
      },
      {
        id: 'miniclip',
        name: 'Miniclip',
        description: 'Jeux flash et HTML5 gratuits depuis 2001.',
        url: 'https://www.miniclip.com',
        icon: 'https://cdn.simpleicons.org/minisf/FF6600?v=1763029785513',
        availability: ['global'],
        tags: ['Classique', 'Gratuit'],
      },
      {
        id: 'armor-games',
        name: 'Armor Games',
        description: 'Portail de jeux indépendants de qualité.',
        url: 'https://armorgames.com',
        icon: 'https://cdn.simpleicons.org/arm/0091BD?v=1763029785513',
        availability: ['global'],
        tags: ['Indie', 'Gratuit'],
      },
      {
        id: 'kongregate',
        name: 'Kongregate',
        description: 'Vaste bibliothèque de jeux navigateur avec succès.',
        url: 'https://www.kongregate.com',
        icon: 'https://cdn.simpleicons.org/kong/002b5c?v=1763029785513',
        availability: ['global'],
        tags: ['Achievements', 'Gratuit'],
      },
      {
        id: 'y8',
        name: 'Y8',
        description: 'Plateforme de jeux en ligne multijoueurs.',
        url: 'https://www.y8.com',
        icon: 'https://cdn.simpleicons.org/y/00FF00?v=1763029785513',
        availability: ['global'],
        tags: ['Multijoueur', 'Gratuit'],
      },
      {
        id: 'newgrounds',
        name: 'Newgrounds',
        description: 'Jeux et animations créés par la communauté.',
        url: 'https://www.newgrounds.com',
        icon: 'https://cdn.simpleicons.org/newgrounds/FF9900?v=1763029785513',
        availability: ['global'],
        tags: ['Community', 'Flash'],
      },
      {
        id: 'addicting-games',
        name: 'Addicting Games',
        description: 'Jeux flash addictifs et populaires.',
        url: 'https://www.addictinggames.com',
        icon: 'https://cdn.simpleicons.org/adguard/68BC71?v=1763029785513',
        availability: ['global'],
        tags: ['Flash', 'Arcade'],
      },
      {
        id: 'kizi',
        name: 'Kizi',
        description: 'Jeux gratuits pour toute la famille.',
        url: 'https://www.kizi.com',
        icon: 'https://cdn-icons-png.flaticon.com/512/7443/7443976.png',
        availability: ['global'],
        tags: ['Famille', 'Gratuit'],
      },
      {
        id: 'friv',
        name: 'Friv',
        description: 'Collection de jeux HTML5 instantanés.',
        url: 'https://www.friv.com',
        icon: 'https://cdn.simpleicons.org/funimation/5B0BB5?v=1763029785513',
        availability: ['global'],
        tags: ['Instantané', 'HTML5'],
      },
      {
        id: 'agame',
        name: 'Agame',
        description: 'Plateforme de jeux en ligne variés.',
        url: 'https://www.agame.com',
        icon: 'https://cdn.simpleicons.org/awesomelists/FC60A8?v=1763029785513',
        availability: ['global'],
        tags: ['Variété', 'Gratuit'],
      },
      {
        id: 'retrogames',
        name: 'RetroGames',
        description: 'Jeux rétro classiques en émulation navigateur.',
        url: 'https://www.retrogames.cc/',
        icon: 'https://cdn.simpleicons.org/retroarch/000000?v=1763029785513',
        availability: ['global'],
        tags: ['Rétro', 'Classique', 'Gratuit'],
      },
      {
        id: 'gamesnacks',
        name: 'GameSnacks',
        description: 'Jeux HTML5 légers et rapides par Google.',
        url: 'https://gamesnacks.com',
        icon: 'https://cdn.simpleicons.org/googlechrome/4285F4?v=1763029785513',
        availability: ['global'],
        tags: ['Google', 'Instantané', 'Gratuit'],
      },
      {
        id: 'webrcade',
        name: 'Webrcade',
        description: 'Émulateur web pour jeux rétro avec manette.',
        url: 'https://www.webrcade.com',
        icon: 'https://cdn.simpleicons.org/webassembly/654FF0?v=1763029785513',
        availability: ['global'],
        tags: ['Émulateur', 'Rétro', 'Manette'],
      },
    ],
  },
  {
    id: 'kids',
    title: 'Famille & enfants',
    subtitle: 'Jeux éducatifs et sûrs pour la cabine XPENG',
    colorFrom: 'from-pink-500',
    colorTo: 'to-rose-400',
    platforms: [
      {
        id: 'lego',
        name: 'LEGO Play',
        description: 'Mini-jeux interactifs LEGO.',
        url: 'https://www.lego.com/en-us/kids',
        icon: 'https://cdn-icons-png.flaticon.com/512/4360/4360686.png',
        availability: ['global'],
      },
      {
        id: 'pbs-kids',
        name: 'PBS Kids Games',
        description: 'Jeux éducatifs adaptés aux plus jeunes.',
        url: 'https://pbskids.org/games',
        icon: 'https://cdn.simpleicons.org/pbs/0063DC?v=1763029785513',
        availability: ['north-america'],
      },
    ],
  },
];

export const chargingCategories: PlatformCategory[] = [
  {
    id: 'xpeng-supercharging',
    title: 'XPENG Supercharging',
    subtitle: 'Trouvez la borne la plus proche de votre itinéraire',
    highlight: 'Réseau 480 kW',
    colorFrom: 'from-slate-800',
    colorTo: 'to-slate-600',
    platforms: [
      {
        id: 'xpeng-map',
        name: 'XPENG Stations',
        description: 'Planifiez et surveillez les superchargers XPENG.',
        url: 'https://www.xpeng.com/supercharging',
        icon: 'https://ui-avatars.com/api/?name=XPENG&background=000000&color=00AEEF&size=128&bold=true&font-size=0.4&v=1763029785513',
        availability: ['china'],
        tags: ['Official'],
      },
      {
        id: 'charger-share',
        name: 'ChargerShare',
        description: 'Partage de bornes XPENG avec réservation anticipée.',
        url: 'https://share.xpeng.com',
        icon: 'https://cdn.simpleicons.org/chargepoint/00E096?v=1763029785513',
        availability: ['china'],
      },
    ],
  },
  {
    id: 'global-charging',
    title: 'Réseaux globaux',
    subtitle: 'Compatibles avec les adaptateurs XPENG',
    colorFrom: 'from-sky-600',
    colorTo: 'to-cyan-500',
    platforms: [
      {
        id: 'plugshare',
        name: 'PlugShare',
        description: 'Carte communautaire de bornes dans le monde.',
        url: 'https://www.plugshare.com',
        icon: '/icons/plugshare.svg',
        availability: ['global'],
        tags: ['Community'],
      },
      {
        id: 'chargepoint',
        name: 'ChargePoint',
        description: 'Réseau européen et nord-américain majeur.',
        url: 'https://www.chargepoint.com',
        icon: 'https://cdn.simpleicons.org/chargepoint/00E096?v=1763029785513',
        availability: ['north-america', 'europe'],
      },
      {
        id: 'tesla-supercharger',
        name: 'Tesla Supercharger (NACS)',
        description: 'Accès via adaptateur XPENG compatible NACS.',
        url: 'https://www.tesla.com/supercharger',
        icon: 'https://cdn.simpleicons.org/tesla/CC0000?v=1763029785513',
        availability: ['north-america', 'europe'],
        tags: ['High Power'],
      },
      {
        id: 'chargemap',
        name: 'Chargemap',
        description: 'Carte collaborative de bornes en Europe avec avis.',
        url: 'https://chargemap.com',
        icon: 'https://logo.clearbit.com/chargemap.com',
        availability: ['europe'],
        tags: ['Community', 'Europe'],
      },
      {
        id: 'waze',
        name: 'Waze',
        description: 'Navigation communautaire en temps réel avec traffic.',
        url: 'https://www.waze.com',
        icon: 'https://cdn.simpleicons.org/waze/00D8FF?v=1763029785513',
        availability: ['global'],
        tags: ['Navigation', 'Traffic'],
      },
      {
        id: 'chargeprice',
        name: 'Chargeprice',
        description: 'Comparez les prix de recharge en temps réel.',
        url: 'https://www.chargeprice.app',
        icon: 'https://cdn.simpleicons.org/priceline/0066CC?v=1763029785513',
        availability: ['europe'],
        tags: ['Prix', 'Comparateur'],
      },
      {
        id: 'abrp',
        name: 'A Better Route Planner',
        description: 'Planificateur d\'itinéraire intelligent pour VE.',
        url: 'https://abetterrouteplanner.com',
        icon: 'https://cdn.simpleicons.org/abbrobotstudio/FF6600?v=1763029785513',
        availability: ['global'],
        tags: ['Navigation', 'Route', 'Planificateur'],
      },
      {
        id: 'total-ev-charge',
        name: 'Total EV Charge',
        description: 'Réseau de bornes Total Energies en Europe.',
        url: 'https://apps.total-ev-charge.com/charge-points',
        icon: 'https://cdn.simpleicons.org/totalenergies/FF6600?v=1763029785513',
        availability: ['europe'],
        tags: ['Total', 'Europe'],
      },
      {
        id: 'google-maps',
        name: 'Google Maps',
        description: 'Navigation et cartographie mondiale avec bornes EV.',
        url: 'https://www.google.com/maps',
        icon: 'https://cdn.simpleicons.org/googlemaps/4285F4?v=1763029785513',
        availability: ['global'],
        tags: ['Navigation', 'Google'],
      },
      {
        id: 'windy',
        name: 'Windy',
        description: 'Météo et vent en temps réel pour planifier vos trajets.',
        url: 'https://www.windy.com',
        icon: 'https://cdn-icons-png.flaticon.com/512/4005/4005817.png',
        availability: ['global'],
        tags: ['Météo', 'Vent'],
      },
      {
        id: 'infotrafic',
        name: 'Info Trafic',
        description: 'État du trafic routier en France en temps réel.',
        url: 'https://www.infotrafic.com',
        icon: 'https://cdn.simpleicons.org/waze/00D8FF?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Trafic', 'France'],
      },
    ],
  },
];

export const otherServicesCategories: PlatformCategory[] = [
  {
    id: 'xpeng-internal-apps',
    title: 'Apps XPENG intégrées',
    subtitle: 'Applications web embarquées qui ne dépendent pas de stores externes',
    colorFrom: 'from-cyan-600',
    colorTo: 'to-emerald-500',
    platforms: [
      {
        id: 'xpeng-iptv-internal',
        name: 'XPENG IPTV Player',
        description: 'Player IPTV complet (M3U & Xtream) optimisé pour navigateur XPENG.',
        url: 'https://dlnraja.github.io/xpengmedia/iptv.html',
        icon: 'https://cdn-icons-png.flaticon.com/512/3845/3845807.png',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'IPTV', 'Interne'],
      },
      {
        id: 'xpeng-web-calculator',
        name: 'XPENG Web Calculator',
        description: 'Calculatrice lisible et tactile pour l’écran embarqué.',
        url: 'https://dlnraja.github.io/xpengmedia/calculator.html',
        icon: 'https://cdn-icons-png.flaticon.com/512/671/671157.png',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Outil', 'Interne'],
      },
      {
        id: 'xpeng-web-notes',
        name: 'XPENG Web Notes',
        description: 'Bloc-notes persistant pour vos trajets, sauvegardé dans le navigateur.',
        url: 'https://dlnraja.github.io/xpengmedia/notes.html',
        icon: 'https://cdn-icons-png.flaticon.com/512/1250/1250615.png',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Notes', 'Interne'],
      },
      {
        id: 'xpeng-timer',
        name: 'XPENG Timer',
        description: 'Chronomètre simple pour pauses, recharges et temps de trajet.',
        url: 'https://dlnraja.github.io/xpengmedia/timer.html',
        icon: 'https://cdn-icons-png.flaticon.com/512/2097/2097029.png',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Temps', 'Interne'],
      },
      {
        id: 'xpeng-pdf-viewer',
        name: 'XPENG PDF Viewer',
        description: 'Outil simple pour afficher des PDFs (manuels, factures, docs) dans le navigateur.',
        url: 'https://dlnraja.github.io/xpengmedia/pdf-viewer.html',
        icon: 'https://cdn-icons-png.flaticon.com/512/337/337946.png',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'PDF', 'Interne'],
      },
      {
        id: 'xpeng-media-guide',
        name: 'XPENG Media Guide',
        description: 'Guide des services vidéo, TV et IPTV optimisé pour l’écran XPENG.',
        url: 'https://dlnraja.github.io/xpengmedia/xpeng-media-guide.html',
        icon: '📺',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Guide', 'Vidéo'],
      },
      {
        id: 'xpeng-dashcam-viewer',
        name: 'XPENG Dashcam Viewer',
        description: 'Lecteur simplifié pour les vidéos dashcam, idéal en stationnement.',
        url: 'https://dlnraja.github.io/xpengmedia/xpeng-dashcam-viewer.html',
        icon: '📹',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Dashcam', 'Vidéo'],
      },
      {
        id: 'xpeng-trip-planner',
        name: 'XPENG Trip Planner',
        description: 'Bloc-notes de trajet minimaliste pour préparer vos déplacements.',
        url: 'https://dlnraja.github.io/xpengmedia/xpeng-trip-planner.html',
        icon: '🧭',
        availability: ['global', 'asia', 'china'],
        tags: ['XPENG', 'Trajet', 'Interne'],
      },
    ],
  },
  {
    id: 'xpeng-documentation',
    title: 'Documentation XPENG',
    subtitle: 'Manuels utilisateurs, guides et ressources officielles',
    highlight: 'Officiel',
    colorFrom: 'from-slate-700',
    colorTo: 'to-slate-500',
    platforms: [
      {
        id: 'xpeng-g3i-manual',
        name: 'XPENG G3i - Manuel',
        description: 'Manuel utilisateur officiel du G3i.',
        url: 'https://www.xpeng.com/support/manuals/g3i',
        icon: 'https://cdn-icons-png.flaticon.com/512/2702/2702154.png',
        availability: ['global', 'asia', 'china'],
        tags: ['Manuel', 'G3i'],
      },
      {
        id: 'xpeng-p5-manual',
        name: 'XPENG P5 - Manuel',
        description: 'Manuel utilisateur officiel du P5.',
        url: 'https://www.xpeng.com/support/manuals/p5',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Manuel', 'P5'],
      },
      {
        id: 'xpeng-g6-manual',
        name: 'XPENG G6 - Manuel',
        description: 'Manuel utilisateur officiel du G6.',
        url: 'https://www.xpeng.com/support/manuals/g6',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Manuel', 'G6'],
      },
      {
        id: 'xpeng-p7-manual',
        name: 'XPENG P7 - Manuel',
        description: 'Manuel utilisateur officiel du P7.',
        url: 'https://www.xpeng.com/support/manuals/p7',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Manuel', 'P7'],
      },
      {
        id: 'xpeng-g9-manual',
        name: 'XPENG G9 - Manuel',
        description: 'Manuel utilisateur officiel du G9.',
        url: 'https://www.xpeng.com/support/manuals/g9',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Manuel', 'G9'],
      },
      {
        id: 'xpeng-xmart-os',
        name: 'Xmart OS - Guide',
        description: 'Guide complet du système Xmart OS.',
        url: 'https://www.xpeng.com/support/xmart-os',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Xmart OS', 'Guide'],
      },
      {
        id: 'xpeng-charging-guide',
        name: 'Guide de Recharge',
        description: 'Tout savoir sur la recharge de votre XPENG.',
        url: 'https://www.xpeng.com/support/charging',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Recharge', 'Guide'],
      },
      {
        id: 'xpeng-xpilot-guide',
        name: 'XPILOT - Guide',
        description: 'Guide complet de l\'assistant de conduite XPILOT.',
        url: 'https://www.xpeng.com/support/xpilot',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['XPILOT', 'Autopilot'],
      },
      {
        id: 'xpeng-faq',
        name: 'FAQ XPENG',
        description: 'Questions fréquentes et réponses officielles.',
        url: 'https://www.xpeng.com/support/faq',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['FAQ', 'Support'],
      },
      {
        id: 'xpeng-service-centers',
        name: 'Centres de Service',
        description: 'Trouvez le centre de service XPENG le plus proche.',
        url: 'https://www.xpeng.com/service-centers',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'asia', 'china'],
        tags: ['Service', 'Réparation'],
      },
      {
        id: 'google-search',
        name: 'Google',
        description: 'Moteur de recherche Google.',
        url: 'https://www.google.com',
        icon: 'https://logo.clearbit.com/google.com',
        availability: ['global'],
        tags: ['Recherche', 'Utilitaire'],
      },
    ],
  },
  {
    id: 'xpeng-community',
    title: 'Communauté XPENG',
    subtitle: 'Chaînes YouTube officielles, créateurs et communauté XPENG',
    highlight: 'Contenu exclusif',
    colorFrom: 'from-cyan-600',
    colorTo: 'to-blue-600',
    platforms: [
      {
        id: 'xpeng-official',
        name: 'XPENG Motors Official',
        description: 'Chaîne YouTube officielle XPENG Motors.',
        url: 'https://www.youtube.com/@XPENGMotors',
        icon: 'https://cdn-icons-png.flaticon.com/512/3097/3097180.png',
        availability: ['global'],
        tags: ['Officiel', 'XPENG'],
      },
      {
        id: 'xpeng-europe',
        name: 'XPENG Europe',
        description: 'Chaîne YouTube officielle XPENG pour l\'Europe.',
        url: 'https://www.youtube.com/@XPENGEurope',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['global', 'europe'],
        tags: ['Officiel', 'Europe'],
      },
      {
        id: 'xpeng-france',
        name: 'XPENG France',
        description: 'Communauté et actualités XPENG France.',
        url: 'https://www.youtube.com/@xpengfrance',
        icon: 'https://logo.clearbit.com/xpeng.com',
        availability: ['europe'],
        tags: ['France', 'Communauté'],
      },
      {
        id: 'xpeng-tech-reviews',
        name: 'XPENG Tech Reviews',
        description: 'Reviews et tests approfondis des véhicules XPENG.',
        url: 'https://www.youtube.com/results?search_query=xpeng+review',
        icon: 'https://logo.clearbit.com/youtube.com',
        availability: ['global'],
        tags: ['Review', 'Tech'],
      },
      {
        id: 'xpeng-owners-club',
        name: 'XPENG Owners Club',
        description: 'Communauté internationale des propriétaires XPENG.',
        url: 'https://www.youtube.com/results?search_query=xpeng+owners',
        icon: 'https://logo.clearbit.com/facebook.com',
        availability: ['global'],
        tags: ['Propriétaires', 'Communauté'],
      },
      {
        id: 'ev-reviewers-xpeng',
        name: 'EV YouTubers - XPENG',
        description: 'Créateurs de contenu spécialisés véhicules électriques.',
        url: 'https://www.youtube.com/results?search_query=xpeng+electric+car',
        icon: 'https://logo.clearbit.com/youtube.com',
        availability: ['global'],
        tags: ['EV', 'Créateurs'],
      },
      {
        id: 'xpeng-accessories-fr',
        name: 'Accessoires XPENG France',
        description: 'Boutiques et accessoires pour XPENG en France.',
        url: 'https://www.greendrive-accessories.com/fr/',
        icon: 'https://logo.clearbit.com/amazon.fr',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Accessoires', 'Shopping'],
      },
      {
        id: 'xpeng-discord',
        name: 'Discord XPENG France',
        description: 'Serveur Discord de la communauté XPENG France.',
        url: 'https://discord.gg/xpengfrance',
        icon: 'https://cdn.simpleicons.org/discord/5865F2?v=1763029785513',
        availability: ['global', 'europe', 'western_europe', 'france'],
        tags: ['Discord', 'Communauté'],
      },
    ],
  },
];
